import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface UseSectionVirtualizationParams {
    /**
     * Total number of sections available (loaded + skeleton).
     */
    totalSections: number;
    /**
     * Number of sections to keep rendered above the viewport.
     * @default 1
     */
    bufferAbove?: number;
    /**
     * Number of sections to keep rendered below the viewport.
     * @default 1
     */
    bufferBelow?: number;
    /**
     * Minimum number of sections to always keep rendered.
     * @default 3
     */
    minVisibleSections?: number;
    /**
     * Enable/disable virtualization.
     * @default true
     */
    enabled?: boolean;
    /**
     * Number of sections at the start that are always rendered (not virtualized).
     * These sections are excluded from top spacer calculations since they exist
     * as real DOM elements. Used for skeleton sections in deep-link scenarios.
     * @default 0
     */
    alwaysRenderedCount?: number;
    /**
     * Ref flag to pin `visibleRange.start` when totalSections increases.
     * Set to `true` before a View More operation to prevent the start index
     * from shifting when new sections are appended below the viewport.
     * The flag is automatically reset to `false` after being consumed.
     */
    lockStartRef?: React.MutableRefObject<boolean>;
}

interface UseSectionVirtualizationReturn {
    /**
     * Range of section indices to render [start, end).
     * end is exclusive (like Array.slice).
     */
    visibleRange: { start: number; end: number };
    /**
     * Register a section element for position tracking.
     * Call with null when the element unmounts.
     */
    registerSection: (index: number, element: HTMLDivElement | null) => void;
    /**
     * Height of the spacer to render above visible sections (for removed sections).
     */
    topSpacerHeight: number;
    /**
     * Height of the spacer to render below visible sections (for removed sections).
     */
    bottomSpacerHeight: number;
    /**
     * Register an extra element's height for a section index.
     * Used for sibling elements (e.g., FeaturedBanner) that are rendered
     * outside the section wrapper but should be included in spacer calculations.
     * Call with null when the element unmounts. The cached height is preserved
     * for accurate spacer calculations when the element is virtualized out.
     */
    registerExtraHeight: (
        index: number,
        element: HTMLDivElement | null,
    ) => void;
    /**
     * Directly register a height value for a section index.
     * Used when we have a measured height but no DOM element to ref
     * (e.g., skeleton pages that wrap multiple virtual section indices).
     */
    registerSectionHeight: (index: number, height: number) => void;
    /**
     * Temporarily disable virtualization (e.g., during programmatic scroll).
     */
    disable: () => void;
    /**
     * Re-enable virtualization after it was disabled.
     */
    enable: () => void;
}

// Estimated section heights for spacer calculation when not measured
const ESTIMATED_SECTION_HEIGHT_MOBILE = 680; // section + padding
const ESTIMATED_SECTION_HEIGHT_DESKTOP = 880; // section + padding

// Hysteresis in pixels for the visible range boundary.
// A section must travel this far past the viewport edge before being removed.
// This prevents oscillation at the boundary from tiny scroll adjustments
// (e.g., from CSS scroll anchoring or subpixel rounding).
const HYSTERESIS_PX = 500;

/**
 * Hook for virtualizing sections in a scrollable grid.
 *
 * This hook tracks which sections are currently in or near the viewport
 * and provides a visible range that can be used to conditionally render
 * only the sections that need to be displayed.
 *
 * When sections outside the visible range are not rendered, spacer elements
 * should be used to maintain the correct scroll position.
 *
 * @example
 * ```tsx
 * const { visibleRange, registerSection, topSpacerHeight, bottomSpacerHeight } =
 *   useSectionVirtualization({ totalSections: 20, enabled: true });
 *
 * return (
 *   <div>
 *     {topSpacerHeight > 0 && <div style={{ height: topSpacerHeight }} />}
 *     {sections
 *       .filter((_, i) => i >= visibleRange.start && i < visibleRange.end)
 *       .map((section, i) => (
 *         <div key={i} ref={(el) => registerSection(i, el)}>
 *           <Section {...section} />
 *         </div>
 *       ))}
 *     {bottomSpacerHeight > 0 && <div style={{ height: bottomSpacerHeight }} />}
 *   </div>
 * );
 * ```
 */
export function useSectionVirtualization({
    totalSections,
    bufferAbove = 1,
    bufferBelow = 1,
    minVisibleSections = 3,
    enabled: initialEnabled = true,
    alwaysRenderedCount = 0,
    lockStartRef,
}: UseSectionVirtualizationParams): UseSectionVirtualizationReturn {
    // State for the visible range
    const [visibleRange, setVisibleRange] = useState({
        start: 0,
        end: totalSections,
    });

    // State for enabling/disabling virtualization
    const [isEnabled, setIsEnabled] = useState(initialEnabled);

    // Refs for tracking section elements and their heights
    const sectionRefs = useRef<Map<number, HTMLDivElement>>(new Map());
    const sectionHeights = useRef<Map<number, number>>(new Map());

    // Extra heights for sibling elements (e.g., FeaturedBanner after section 0).
    // These are elements rendered outside the section wrapper but associated with a section.
    const extraHeights = useRef<Map<number, number>>(new Map());

    // RAF ID for throttling scroll events
    const rafId = useRef<number | null>(null);

    // Register a section element for tracking
    const registerSection = useCallback(
        (index: number, element: HTMLDivElement | null) => {
            if (element) {
                sectionRefs.current.set(index, element);
                // Always re-measure height when a section (re-)mounts.
                // Images may have loaded since the last measurement, changing
                // the section height. Stale cached heights cause spacer drift
                // that manifests as tile flashing when scrolling back up.
                const height = element.getBoundingClientRect().height;
                if (height > 0) {
                    sectionHeights.current.set(index, height);
                }
            } else {
                sectionRefs.current.delete(index);
                // Preserve cached height for accurate spacers when section is virtualized out.
                // The layout height doesn't change — deleting it forces fallback to static
                // estimates (680/880px), which compounds into large spacer drift.
            }
        },
        [],
    );

    // Register an extra element's height associated with a section index.
    // Used for sibling elements (e.g., FeaturedBanner) that disappear
    // when their associated section is virtualized.
    const registerExtraHeight = useCallback(
        (index: number, element: HTMLDivElement | null) => {
            if (element) {
                const height = element.getBoundingClientRect().height;
                if (height > 0) {
                    extraHeights.current.set(index, height);
                }
            } else {
                // Do nothing - Preserve cached height for accurate spacers when element is virtualized out.
            }
        },
        [],
    );

    // Directly register a height value for a section index.
    // Used for skeleton pages that wrap multiple virtual section indices —
    // we assign the full measured height to the first index and 0 to the rest.
    const registerSectionHeight = useCallback(
        (index: number, height: number) => {
            // Allow 0 — skeleton pages assign full height to the first virtual
            // index and 0 to the rest so getEstimatedHeight skips them.
            sectionHeights.current.set(index, height);
        },
        [],
    );

    // Get estimated height for a section (used when actual height isn't measured).
    // Includes any extra height from sibling elements (e.g., FeaturedBanner).
    const getEstimatedHeight = useCallback((index: number) => {
        const extra = extraHeights.current.get(index) ?? 0;

        // Check if we have a cached measurement (use .has() because 0 is a valid
        // cached height for skeleton "extra" indices that intentionally contribute 0).
        if (sectionHeights.current.has(index)) {
            return sectionHeights.current.get(index)! + extra;
        }

        // Fall back to estimate based on viewport width
        if (typeof window === 'undefined')
            return ESTIMATED_SECTION_HEIGHT_DESKTOP + extra;
        return (
            (window.innerWidth < 768
                ? ESTIMATED_SECTION_HEIGHT_MOBILE
                : ESTIMATED_SECTION_HEIGHT_DESKTOP) + extra
        );
    }, []);

    // Calculate visible range based on scroll position and estimated heights
    // This approach works without requiring all sections to be registered,
    // using cumulative estimated heights to determine which sections are in view.
    const calculateVisibleRange = useCallback(() => {
        // If disabled or no sections, show all
        if (!isEnabled || totalSections === 0) {
            return { start: 0, end: totalSections };
        }

        const scrollTop = window.scrollY;
        const viewportHeight = window.innerHeight;
        const viewportBottom = scrollTop + viewportHeight;

        // Expand the "keep alive" window by HYSTERESIS_PX on each side.
        // A section must travel this far past the viewport before being removed,
        // preventing oscillation from tiny scroll adjustments (e.g., scroll anchoring).
        const hysteresisTop = scrollTop - HYSTERESIS_PX;
        const hysteresisBottom = viewportBottom + HYSTERESIS_PX;

        let firstVisibleIndex = 0;
        let lastVisibleIndex = totalSections - 1;
        let cumulativeHeight = 0;
        let foundFirst = false;

        // Calculate which sections are in the viewport using cumulative heights
        for (let i = 0; i < totalSections; i++) {
            const sectionHeight = getEstimatedHeight(i);
            const sectionTop = cumulativeHeight;
            const sectionBottom = cumulativeHeight + sectionHeight;

            // Find first section that extends into or past the viewport top (with hysteresis)
            if (!foundFirst && sectionBottom >= hysteresisTop) {
                firstVisibleIndex = Math.max(0, i - bufferAbove);
                foundFirst = true;
            }

            // Find last section that starts before the viewport bottom (with hysteresis)
            if (sectionTop <= hysteresisBottom) {
                lastVisibleIndex = Math.min(totalSections - 1, i + bufferBelow);
            }

            cumulativeHeight += sectionHeight;

            // Early exit: once we've passed the hysteresis window, no need to continue
            if (sectionTop > hysteresisBottom) {
                break;
            }
        }

        // Enforce minimum visible sections
        const rangeSize = lastVisibleIndex - firstVisibleIndex + 1;
        if (rangeSize < minVisibleSections) {
            // Expand the range to meet minimum
            const expansion = minVisibleSections - rangeSize;
            // Try to expand downward first, then upward
            const expandDown = Math.min(
                expansion,
                totalSections - 1 - lastVisibleIndex,
            );
            lastVisibleIndex += expandDown;

            const expandUp = expansion - expandDown;
            firstVisibleIndex = Math.max(0, firstVisibleIndex - expandUp);
        }

        return { start: firstVisibleIndex, end: lastVisibleIndex + 1 };
    }, [
        isEnabled,
        totalSections,
        bufferAbove,
        bufferBelow,
        minVisibleSections,
        getEstimatedHeight,
    ]);

    // Scroll handler with RAF throttling
    useEffect(() => {
        if (!isEnabled) return;

        const handleScroll = () => {
            // Cancel any pending RAF
            if (rafId.current !== null) {
                cancelAnimationFrame(rafId.current);
            }

            rafId.current = requestAnimationFrame(() => {
                const newRange = calculateVisibleRange();

                setVisibleRange((prev) => {
                    // Only update if range actually changed
                    if (
                        prev.start === newRange.start &&
                        prev.end === newRange.end
                    ) {
                        return prev;
                    }
                    return newRange;
                });

                rafId.current = null;
            });
        };

        window.addEventListener('scroll', handleScroll, { passive: true });

        // Also handle resize events (debounced)
        let resizeTimeout: ReturnType<typeof setTimeout> | null = null;
        const handleResize = () => {
            if (resizeTimeout) clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                // Clear cached heights on resize (they may have changed)
                sectionHeights.current.clear();
                extraHeights.current.clear();
                handleScroll();
            }, 150);
        };

        window.addEventListener('resize', handleResize, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleResize);
            if (rafId.current !== null) {
                cancelAnimationFrame(rafId.current);
                rafId.current = null;
            }
            // Clear resize debounce timeout to prevent memory leaks
            if (resizeTimeout) {
                clearTimeout(resizeTimeout);
                resizeTimeout = null;
            }
        };
    }, [isEnabled, calculateVisibleRange]);

    // Recalculate range when totalSections changes (e.g., new pages loaded).
    // Using calculateVisibleRange() avoids the expand-then-contract flash
    // that happened when unconditionally expanding to totalSections.
    // Uses functional update to prevent re-render when the range hasn't changed.
    // When lockStartRef is set (View More), pins start to prevent visible sections
    // from unmounting/remounting when new sections are appended below.
    useEffect(() => {
        // Capture and consume the lock flag before setState to avoid
        // side-effects inside the updater (React Strict Mode may call it twice).
        const shouldLockStart = lockStartRef?.current === true;
        if (shouldLockStart && lockStartRef) {
            lockStartRef.current = false;
        }

        setVisibleRange((prev) => {
            const next = calculateVisibleRange();
            // View More: pin start index to prevent flash of currently-visible sections.
            // Only expand end to include newly appended sections.
            if (shouldLockStart && next.end >= prev.end) {
                return { start: prev.start, end: next.end };
            }
            if (prev.start === next.start && prev.end === next.end) return prev;
            return next;
        });
    }, [totalSections, calculateVisibleRange, lockStartRef]);

    // Update enabled state when prop changes
    useEffect(() => {
        setIsEnabled(initialEnabled);
    }, [initialEnabled]);

    // Calculate spacer heights
    const topSpacerHeight = useMemo(() => {
        if (!isEnabled || visibleRange.start === 0) return 0;

        let height = 0;
        // Start from alwaysRenderedCount — sections before this are always
        // in the DOM (e.g., skeleton sections) and must not be double-counted
        // by the spacer. Only sum heights for virtualized (non-rendered) sections.
        const spacerStart = Math.max(alwaysRenderedCount, 0);
        for (let i = spacerStart; i < visibleRange.start; i++) {
            height += getEstimatedHeight(i);
        }
        return height;
    }, [
        isEnabled,
        visibleRange.start,
        getEstimatedHeight,
        alwaysRenderedCount,
    ]);

    const bottomSpacerHeight = useMemo(() => {
        if (!isEnabled || visibleRange.end >= totalSections) return 0;

        let height = 0;
        for (let i = visibleRange.end; i < totalSections; i++) {
            height += getEstimatedHeight(i);
        }
        return height;
    }, [isEnabled, visibleRange.end, totalSections, getEstimatedHeight]);

    // Control functions
    const disable = useCallback(() => {
        setIsEnabled(false);
        setVisibleRange({ start: 0, end: totalSections });
    }, [totalSections]);

    const enable = useCallback(() => {
        setIsEnabled(true);
    }, []);

    return {
        visibleRange,
        registerSection,
        registerExtraHeight,
        registerSectionHeight,
        topSpacerHeight,
        bottomSpacerHeight,
        disable,
        enable,
    };
}
